import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  PauseCircle,
  SkipForward,
  Activity,
  BarChart3,
  Calendar,
  Building2,
  Wrench,
  AlertCircle
} from 'lucide-react';
import Chart from 'react-apexcharts';
import projectService, {
  type ProjectResourceProgressDetail,
  type ProjectResourceRow,
  type ResourceTaskGroupNode,
  type ResourceTaskItem
} from '../../../../services/projectService';
import TaskDetailDrawer from './TaskDetailDrawer';

interface Props {
  projectId: string;
  resource: ProjectResourceRow | null;
  onClose: () => void;
}

const STATUS_TABS = [
  { key: 'completed', label: 'Completed', icon: CheckCircle2, tone: 'emerald' },
  { key: 'inProgress', label: 'In Progress', icon: Clock, tone: 'cyan' },
  { key: 'pending', label: 'Pending', icon: PauseCircle, tone: 'amber' },
  { key: 'overdue', label: 'Overdue', icon: AlertTriangle, tone: 'rose' },
  { key: 'failed', label: 'Failed', icon: XCircle, tone: 'rose' },
  { key: 'skipped', label: 'Skipped', icon: SkipForward, tone: 'slate' }
] as const;

type StatusKey = typeof STATUS_TABS[number]['key'];

const TONE_BG: Record<typeof STATUS_TABS[number]['tone'], string> = {
  emerald: 'bg-emerald-500 text-white',
  cyan: 'bg-cyan-500 text-white',
  amber: 'bg-amber-500 text-white',
  rose: 'bg-rose-500 text-white',
  slate: 'bg-slate-500 text-white'
};

function formatMinutes(min: number): string {
  if (!min || min <= 0) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export default function ResourceProgressDetail({ projectId, resource, onClose }: Props) {
  const [detail, setDetail] = useState<ProjectResourceProgressDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<StatusKey>('completed');
  const [taskOpen, setTaskOpen] = useState<ResourceTaskItem | null>(null);

  useEffect(() => {
    if (!resource) return;
    setLoading(true);
    setError(null);
    setDetail(null);
    projectService
      .getProgressResourceDetail(projectId, resource.resourceId)
      .then(res => {
        setDetail(res);
        // pick first non-empty status as the default tab
        const firstWithItems = res.groupedByStatus.find(g => g.count > 0);
        if (firstWithItems) setActiveTab(firstWithItems.status);
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load resource detail'))
      .finally(() => setLoading(false));
  }, [projectId, resource]);

  const activeGroup = useMemo(() => {
    if (!detail) return null;
    return detail.groupedByStatus.find(g => g.status === activeTab) ?? null;
  }, [detail, activeTab]);

  return (
    <AnimatePresence>
      {resource && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 220, damping: 28 }}
            onClick={e => e.stopPropagation()}
            className="flex h-full w-full max-w-5xl flex-col bg-slate-50 shadow-2xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-6 py-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white shadow-sm"
                  style={{ background: resource.avatarColor }}
                >
                  {resource.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-teal-600">Resource progress</p>
                  <h2 className="text-lg font-bold text-slate-900">{resource.name}</h2>
                  <p className="text-xs text-slate-500">
                    {resource.email}
                    {resource.roleName ? ` · ${resource.roleName}` : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            {loading && (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
                  <p className="text-sm text-slate-500">Loading tasks…</p>
                </div>
              </div>
            )}

            {error && (
              <div className="m-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                <AlertCircle className="mb-1 h-5 w-5" /> {error}
              </div>
            )}

            {detail && !loading && (
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <KPISection detail={detail} />

                {/* Status tabs */}
                <nav className="mb-3 mt-6 flex flex-wrap items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
                  {STATUS_TABS.map(tab => {
                    const group = detail.groupedByStatus.find(g => g.status === tab.key);
                    const count = group?.count ?? 0;
                    const Icon = tab.icon;
                    const active = activeTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                        disabled={count === 0}
                        className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
                          active
                            ? TONE_BG[tab.tone]
                            : count === 0
                              ? 'cursor-not-allowed text-slate-400'
                              : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {tab.label}
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-slate-200 text-slate-700'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </nav>

                {/* Tree */}
                <div className="rounded-xl border border-slate-200 bg-white">
                  {!activeGroup || activeGroup.count === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                      <CheckCircle2 className="mb-2 h-8 w-8 text-slate-300" />
                      <p className="text-sm">No {activeGroup?.label.toLowerCase() ?? ''} tasks for this engineer.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {activeGroup.tree.map(node => (
                        <GroupNode
                          key={`${node.level}-${node.key}`}
                          node={node}
                          depth={0}
                          onTaskClick={setTaskOpen}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}

      <TaskDetailDrawer task={taskOpen} onClose={() => setTaskOpen(null)} />
    </AnimatePresence>
  );
}

function KPISection({ detail }: { detail: ProjectResourceProgressDetail }) {
  const t = detail.totals;
  const dailyOptions: ApexCharts.ApexOptions = {
    chart: { type: 'bar', height: 220, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 4, borderRadiusApplication: 'end' } },
    colors: ['#10B981', '#F43F5E'],
    dataLabels: { enabled: false },
    xaxis: {
      categories: detail.kpis.dailyCompletion.map(d => d.date.slice(5)),
      labels: { style: { colors: '#64748B', fontSize: '10px' } },
      axisTicks: { show: false },
      axisBorder: { show: false }
    },
    yaxis: { labels: { style: { colors: '#64748B' } } },
    grid: { borderColor: '#E2E8F0', strokeDashArray: 4 },
    legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px' },
    tooltip: { theme: 'light' }
  };
  const dailySeries = [
    { name: 'Completed', data: detail.kpis.dailyCompletion.map(d => d.completed) },
    { name: 'Failed', data: detail.kpis.dailyCompletion.map(d => d.failed) }
  ];

  const hourOptions: ApexCharts.ApexOptions = {
    chart: { type: 'area', height: 220, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    stroke: { curve: 'smooth', width: 2 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
    colors: ['#0EA5E9'],
    dataLabels: { enabled: false },
    xaxis: {
      categories: detail.kpis.hourOfDayActivity.map(h => `${h.hour}:00`),
      labels: { style: { colors: '#64748B', fontSize: '10px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: '#64748B' } } },
    grid: { borderColor: '#E2E8F0', strokeDashArray: 4 },
    tooltip: { theme: 'light' }
  };
  const hourSeries = [{ name: 'Activity', data: detail.kpis.hourOfDayActivity.map(h => h.count) }];

  const bucketOptions: ApexCharts.ApexOptions = {
    chart: { type: 'bar', height: 220, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
    colors: ['#8B5CF6'],
    dataLabels: { enabled: true, style: { fontSize: '10px', colors: ['#fff'] } },
    xaxis: {
      categories: detail.kpis.workingTimeBuckets.map(b => b.bucket),
      labels: { style: { colors: '#64748B', fontSize: '10px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: { labels: { style: { colors: '#64748B', fontSize: '10px' } } },
    grid: { borderColor: '#E2E8F0', strokeDashArray: 4 },
    tooltip: { theme: 'light' }
  };
  const bucketSeries = [{ name: 'Tasks', data: detail.kpis.workingTimeBuckets.map(b => b.count) }];

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <KPICard label="Total" value={t.totalAssigned} icon={Activity} tone="slate" />
        <KPICard label="Completed" value={t.completed} icon={CheckCircle2} tone="emerald" />
        <KPICard label="Pending" value={t.pending} icon={PauseCircle} tone="amber" />
        <KPICard label="Overdue" value={t.overdue} icon={AlertTriangle} tone="rose" />
        <KPICard label="Avg time" value={formatMinutes(t.avgCompletionMinutes)} icon={Clock} tone="cyan" />
        <KPICard label="Median" value={formatMinutes(t.medianCompletionMinutes)} icon={Clock} tone="violet" />
        <KPICard label="Completion" value={`${t.completionRate}%`} icon={BarChart3} tone="teal" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard title="Daily completion (last 14 days)">
          <Chart options={dailyOptions} series={dailySeries} type="bar" height={220} />
        </ChartCard>
        <ChartCard title="Activity by hour of day">
          <Chart options={hourOptions} series={hourSeries} type="area" height={220} />
        </ChartCard>
        <ChartCard title="Working time per task">
          <Chart options={bucketOptions} series={bucketSeries} type="bar" height={220} />
          <p className="mt-1 text-[10px] text-slate-500">
            Fastest {formatMinutes(detail.kpis.fastestCompletionMinutes)} ·
            Slowest {formatMinutes(detail.kpis.slowestCompletionMinutes)}
          </p>
        </ChartCard>
      </div>
    </>
  );
}

function KPICard({
  label,
  value,
  icon: Icon,
  tone
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'cyan' | 'emerald' | 'amber' | 'rose' | 'slate' | 'violet' | 'teal';
}) {
  const tones = {
    cyan: 'text-cyan-700 bg-cyan-500/10',
    emerald: 'text-emerald-700 bg-emerald-500/10',
    amber: 'text-amber-700 bg-amber-500/10',
    rose: 'text-rose-700 bg-rose-500/10',
    slate: 'text-slate-700 bg-slate-500/10',
    violet: 'text-violet-700 bg-violet-500/10',
    teal: 'text-teal-700 bg-teal-500/10'
  } as const;
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <div className="flex items-center justify-between">
        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${tones[tone]}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      </div>
      <p className="mt-2 text-lg font-bold text-slate-900">{value}</p>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <h3 className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">{title}</h3>
      {children}
    </div>
  );
}

function GroupNode({
  node,
  depth,
  onTaskClick
}: {
  node: ResourceTaskGroupNode;
  depth: number;
  onTaskClick: (task: ResourceTaskItem) => void;
}) {
  const [open, setOpen] = useState(depth === 0);

  if (node.level === 'task') {
    return (
      <button
        type="button"
        onClick={() => onTaskClick(node.task)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
        style={{ paddingLeft: `${12 + depth * 16}px` }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Wrench className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-slate-800">{node.label}</p>
            <p className="text-[10px] text-slate-500">
              {node.task.frequency ? `${node.task.frequency} · ` : ''}
              {node.task.actualWorkingMinutes > 0 ? `Worked ${formatMinutes(node.task.actualWorkingMinutes)}` : 'No working time'}
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1 text-[10px] text-slate-400">
          {node.task.hasIssue && (
            <span className="rounded-full bg-rose-500/10 px-1.5 py-0.5 font-bold text-rose-700">issue</span>
          )}
          <ChevronRight className="h-3 w-3" />
        </span>
      </button>
    );
  }

  const Icon = node.level === 'date' ? Calendar : node.level === 'asset' ? Building2 : Wrench;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
        style={{ paddingLeft: `${12 + depth * 16}px` }}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
        <Icon className="h-3.5 w-3.5 text-teal-600" />
        <span className="flex-1 truncate text-xs font-bold text-slate-800">{node.label}</span>
        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">
          {node.taskCount}
        </span>
      </button>
      {open && (
        <div className="border-l border-slate-100" style={{ marginLeft: `${12 + depth * 16}px` }}>
          {node.children.map(child => (
            <GroupNode
              key={`${child.level}-${child.key}`}
              node={child}
              depth={depth + 1}
              onTaskClick={onTaskClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
